import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { HeatBracket, Player, Tournament } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import { publicApiClient } from '@iziping/common';
import { useTranslation } from 'react-i18next';
import { InputIcon } from "@iziping/ui";
import { GitBranch, MoveHorizontal, Search, Trophy, Users } from 'lucide-react';
import HeatBracketDisplay from '../../../components/bracket/HeatBracketDisplay';
import styles from './TournamentViewPage.module.css';

type Tab = 'players' | 'bracket';

const TournamentViewPage: React.FC = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tournamentId = id ? parseInt(id, 10) : 0;
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<Tab>('players');
    const [selectedHeatId, setSelectedHeatId] = useState<number | undefined>(undefined);
    const [bracket, setBracket] = useState<HeatBracket | null>(null);
    const [bracketLoading, setBracketLoading] = useState(false);

    const {
        data: registrations,
        isLoading,
        isError,
    } = useQuery<Player[]>({
        queryKey: [QUERY_CACHE.TOURNAMENT_REGISTRATIONS, tournamentId, 'view'],
        queryFn: async () => {
            const response = await publicApiClient.get<Player[]>(ENDPOINTS.tournamentRegistrations(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });

    const { data: tournament } = useQuery<Tournament>({
        queryKey: [QUERY_CACHE.TOURNAMENT, tournamentId, 'view'],
        queryFn: async () => {
            const response = await publicApiClient.get<Tournament>(ENDPOINTS.tournament(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });

    useEffect(() => {
        if (!selectedHeatId) return;
        setBracketLoading(true);
        publicApiClient
            .get<HeatBracket>(ENDPOINTS.getFinalBracketForHeat(selectedHeatId))
            .then((res) => setBracket(res.data))
            .catch(() => setBracket(null))
            .finally(() => setBracketLoading(false));
    }, [selectedHeatId]);

    const handlePlayerClick = (playerId: number) => {
        navigate(`/tournament/${id}/view/${playerId}${window.location.hash}`);
    };

    const filteredPlayers =
        registrations?.filter((player) => {
            if (!search) return true;
            const term = search.toLowerCase();
            return player.lastname.toLowerCase().includes(term) || player.firstname.toLowerCase().includes(term);
        }) ?? [];

    if (isLoading) return <div className={styles.loadingText}>{t('REGISTRATION_LOADING')}</div>;
    if (isError) return <div className={styles.loadingText}>Accès non autorisé</div>;

    return (
        <div className={styles.pageWrapper}>
            {/* Header + tabs : toujours dans le container centré */}
            <div className={styles.container}>
                <div className={styles.header}>
                    {tournament?.branding?.logoUrl ? (
                        <img src={tournament.branding.logoUrl} alt="logo" className={styles.headerLogo} />
                    ) : (
                        <div className={styles.headerIcon}>
                            <Trophy size={28} />
                        </div>
                    )}
                    <h1 className={styles.headerTitle}>{tournament?.name ?? t('tournament.view.title')}</h1>
                </div>

                <div className={styles.tabNav}>
                    <button
                        className={`${styles.tabButton}${activeTab === 'players' ? ` ${styles.tabButtonActive}` : ''}`}
                        onClick={() => setActiveTab('players')}
                    >
                        <Users size={15} />
                        {t('PLAYER_REGISTERED')}
                    </button>
                    <button
                        className={`${styles.tabButton}${activeTab === 'bracket' ? ` ${styles.tabButtonActive}` : ''}`}
                        onClick={() => setActiveTab('bracket')}
                    >
                        <GitBranch size={15} />
                        {t('tournament.view.bracket.tab')}
                    </button>
                </div>

                {/* Liste joueurs : dans le container */}
                {activeTab === 'players' && (
                    <div className={styles.content}>
                        <div className={styles.contentHeader}>
                            <span className={styles.playerCount}>
                                {registrations?.length || 0} {t('PLAYER_REGISTERED')}
                            </span>
                            <div className={styles.searchWrapper}>
                                <InputIcon
                                    icon={Search}
                                    placeholder={t('PLAYER_SEARCH')}
                                    type="text"
                                    value={search}
                                    small={true}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        {filteredPlayers.length > 0 ? (
                            <table className={styles.playerTable}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{t('PLAYER_LASTNAME')}</th>
                                        <th>{t('PLAYER_FIRSTNAME')}</th>
                                        <th>{t('PLAYER_POINTS')}</th>
                                        <th>{t('PLAYER_CLUB')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPlayers.map((player, index) => (
                                        <tr key={player.id} onClick={() => handlePlayerClick(player?.id || 0)}>
                                            <td className={styles.colRank}>{index + 1}</td>
                                            <td className={styles.colName}>{player.lastname}</td>
                                            <td>{player.firstname}</td>
                                            <td>
                                                <span className={styles.pointsBadge}>{player.points}</span>
                                            </td>
                                            <td>{player?.club?.name || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>
                                <Users size={40} />
                                <p>{t('PLAYER_NO_REGISTRATION')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Bracket : pleine largeur, en dehors du container */}
            {activeTab === 'bracket' && (
                <div className={styles.bracketCard}>
                    <div className={styles.bracketCardHeader}>
                        <div className="select-wrapper">
                            <select
                                value={selectedHeatId ?? ''}
                                onChange={(e) => setSelectedHeatId(e.target.value ? Number(e.target.value) : undefined)}
                            >
                                <option value="">{t('HEAT_SELECTOR_TITLE')}</option>
                                {tournament?.heats?.map((heat) => (
                                    <option key={heat.id} value={heat.id}>
                                        {heat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {bracketLoading ? (
                        <p className={styles.loadingText}>{t('LOADING_HEAT_BRACKET')}</p>
                    ) : selectedHeatId && bracket ? (
                        <>
                            <div className={styles.bracketWrapper}>
                                <HeatBracketDisplay bracket={bracket} />
                            </div>
                            <div className={styles.bracketScrollHint}>
                                <MoveHorizontal size={14} />
                                {t('tournament.view.bracket.scrollHint')}
                            </div>
                        </>
                    ) : (
                        <div className={styles.bracketPlaceholder}>
                            <GitBranch size={40} />
                            <p>{t('tournament.view.bracket.empty')}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TournamentViewPage;
